/**
 * Wheel of Fortune (reference: a spin costs one chance; chances are bought with hearts or won back on the wheel).
 *
 * The server draws the prize with `pickWheelPrize`; the odds below are the only odds and are shown to players in the
 * wheel's help. The expected value of a spin in hearts (about ❤2.5 counting won-back chances) stays below the cheapest
 * chance price (❤3), so buying chances to farm hearts never pays off.
 */

export type BoosterCode = 'x2' | 'limit' | 'bonus';

export interface WheelPrize {
  code: string;
  /** Out of WHEEL_TOTAL_WEIGHT. */
  weight: number;
  hearts?: number;
  chances?: number;
  booster?: BoosterCode;
  /** Announced to everyone online (reference: the "Winner … hit the jackpot" banner). */
  announce?: boolean;
}

export const WHEEL_PRIZES: readonly WheelPrize[] = [
  { code: 'hearts_3', weight: 2600, hearts: 3 },
  { code: 'hearts_5', weight: 1400, hearts: 5 },
  { code: 'hearts_25', weight: 120, hearts: 25 },
  { code: 'hearts_100', weight: 15, hearts: 100, announce: true },
  { code: 'jackpot', weight: 1, hearts: 1000, announce: true },
  { code: 'chance_1', weight: 1100, chances: 1 },
  { code: 'chance_3', weight: 250, chances: 3 },
  { code: 'boost_x2', weight: 1500, booster: 'x2' },
  { code: 'boost_limit', weight: 1500, booster: 'limit' },
  { code: 'boost_bonus', weight: 1514, booster: 'bonus' },
];

export const WHEEL_TOTAL_WEIGHT = WHEEL_PRIZES.reduce((sum, p) => sum + p.weight, 0);

/** The wheel as drawn, clockwise from the top; a prize may appear on several segments. */
export const WHEEL_SEGMENTS: readonly string[] = [
  'hearts_3',
  'boost_x2',
  'hearts_5',
  'chance_1',
  'hearts_25',
  'boost_limit',
  'hearts_3',
  'jackpot',
  'hearts_5',
  'boost_bonus',
  'hearts_100',
  'chance_3',
];

export function findWheelPrize(code: string): WheelPrize | undefined {
  return WHEEL_PRIZES.find((p) => p.code === code);
}

/** Maps a uniform integer in [0, WHEEL_TOTAL_WEIGHT) to a prize. */
export function pickWheelPrize(roll: number): WheelPrize {
  let left = roll;
  for (const prize of WHEEL_PRIZES) {
    if (left < prize.weight) return prize;
    left -= prize.weight;
  }
  return WHEEL_PRIZES[0]!;
}

/** Chance of each prize in percent, for the help screen. */
export function wheelOdds(): { code: string; percent: number }[] {
  return WHEEL_PRIZES.map((p) => ({ code: p.code, percent: (p.weight / WHEEL_TOTAL_WEIGHT) * 100 }));
}

/** Chance packs of the "Shop of Chances", priced in GM as in the reference. */
export const CHANCE_PACKS = [
  { code: 'chances_300', chances: 300, gm: 900 },
  { code: 'chances_50', chances: 50, gm: 150 },
  { code: 'chances_15', chances: 15, gm: 45 },
  { code: 'chances_5', chances: 5, gm: 20 },
  { code: 'chances_1', chances: 1, gm: 5 },
] as const;

export function findChancePack(code: string) {
  return CHANCE_PACKS.find((p) => p.code === code);
}

/** How long the client's wheel spins before it shows the prize. */
export const WHEEL_SPIN_MS = 4500;

/** League boosters (won on the wheel, used from the league screen). */
export const BOOSTERS: Record<BoosterCode, { icon: string; minutes?: number; amount?: number }> = {
  /** Kisses received count double for 5 minutes (kiss gifts excluded). */
  x2: { icon: '⚡', minutes: 5 },
  /** Today's cap on points from received kisses goes up by 10. */
  limit: { icon: '📈', amount: 10 },
  /** +5 league points at once, beyond any cap. */
  bonus: { icon: '⭐', amount: 5 },
};
